import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { syncVisitQueue, visitQueue } from './offline';

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

export function useVisitQueue() {
  return useQuery({
    queryKey: ['visit-queue'],
    queryFn: visitQueue,
    refetchInterval: 3_000,
  });
}

export function useAutomaticSync(ownerUserId: string, enabled = true): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let syncing = false;
    const sync = async () => {
      if (syncing || !navigator.onLine) return;
      syncing = true;
      try {
        const result = await syncVisitQueue(ownerUserId);
        if (result.created > 0 || result.duplicated > 0 || result.failed > 0) {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['visit-queue'] }),
            queryClient.invalidateQueries({ queryKey: ['locations'] }),
            queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
            queryClient.invalidateQueries({ queryKey: ['runs'] }),
            queryClient.invalidateQueries({ queryKey: ['inventory'] }),
          ]);
        }
      } finally {
        syncing = false;
      }
    };
    const listener = () => void sync();
    window.addEventListener('online', listener);
    const interval = window.setInterval(listener, 15_000);
    void sync();
    return () => {
      window.removeEventListener('online', listener);
      window.clearInterval(interval);
    };
  }, [enabled, ownerUserId, queryClient]);
}
